import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { getList } from "@Api/Admin/instantDealsWon";
import LoadingFallback from "@Components/Pages/Fallbacks/Loading";
import GetPath from "@Helpers/GetPath";
import { Platform } from "@Shared/Entities/Supplier/InstantPackage/enums";
import { INSTANT_DEALS_WON_DETAIL } from "@Shared/Paths/Admin";
import { networkNames } from "../../../Supplier/Pages/PredictInstant/AmendTariffs/networkConfig";
import { normalizeNetworkId, } from "../../../Supplier/Pages/PredictInstant/AmendTariffs/types";
import { SearchSm } from "../../../Supplier/Pages/PredictInstant/components/icons";
import iconStyles from "../../../Supplier/Pages/PredictInstant/components/icons/index.module.scss";
import { formatDealSummary, formatDealSummaryFromObject, getCustomerEmail, getCustomerName, getDealId, getParameters, getSupplierName, mapStatus, parseDealDate, } from "../../../Supplier/Pages/PredictInstant/DealsWon/chosenPackageHelpers";
import FilterSelect from "../../../Supplier/Pages/PredictInstant/DealsWon/FilterSelect";
import DealsTable from "./DealsTable";
import styles from "./index.module.scss";
const STATUS_OPTIONS = [
    { value: "all", label: "Status: All" },
    { value: "contacted", label: "Status: Contacted" },
    { value: "contractPending", label: "Status: Contract Pending" },
    { value: "contractComplete", label: "Status: Contract Complete" },
];
const NETWORK_OPTIONS = [
    { value: "all", label: "Network: All" },
    ...Object.values(Platform).map((platform) => ({
        value: platform,
        label: `Network: ${networkNames[platform]}`,
    })),
];
const DATE_OPTIONS = [
    { value: "newest", label: "Date: Newest" },
    { value: "oldest", label: "Date: Oldest" },
];
export const getSupplierCompanyNumber = (raw) => {
    if (typeof raw.supplierCompanyNumber === "string") {
        return raw.supplierCompanyNumber.trim();
    }
    const supplier = raw.supplier;
    if (supplier && typeof supplier === "object") {
        const cn = supplier.companyNumber;
        if (typeof cn === "string")
            return cn.trim();
    }
    return "";
};
const mapToAdminDeal = (raw) => {
    const isListPackage = typeof raw.network === "string" ||
        (raw.dealSummary !== undefined && typeof raw.dealSummary === "object");
    let networkId;
    let id;
    if (isListPackage) {
        networkId = normalizeNetworkId(String(raw.network ?? ""));
        id = getDealId(raw);
    }
    else {
        const parameters = getParameters(raw);
        networkId = normalizeNetworkId(String(parameters?.platform ?? ""));
        id = getDealId(raw);
    }
    if (!networkId || !id)
        return null;
    const dealSummary = isListPackage && raw.dealSummary && typeof raw.dealSummary === "object"
        ? formatDealSummaryFromObject(raw.dealSummary)
        : formatDealSummary(raw);
    const supplierName = getSupplierName(raw);
    const supplierCompanyNumber = getSupplierCompanyNumber(raw);
    const supplierInfo = supplierCompanyNumber && supplierName !== "—"
        ? `${supplierCompanyNumber} - ${supplierName}`
        : supplierName;
    return {
        id,
        date: parseDealDate(raw),
        customerName: getCustomerName(raw),
        customerEmail: getCustomerEmail(raw),
        dealSummary,
        networkId,
        status: mapStatus(raw.status ?? raw.dealStatus),
        supplierInfo,
    };
};
const SEARCH_DEBOUNCE_MS = 300;
export default () => {
    const history = useHistory();
    const [deals, setDeals] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [networkFilter, setNetworkFilter] = useState("all");
    const [dateSort, setDateSort] = useState("newest");
    const [page, setPage] = useState(1);
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput);
            setPage(1);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchInput]);
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const { packages, totalPages: pages } = await getList(page, 10, {
                    search: search || undefined,
                    status: statusFilter === "all" ? undefined : statusFilter,
                    network: networkFilter === "all" ? undefined : networkFilter,
                    sort: dateSort,
                });
                if (!cancelled) {
                    const mapped = packages
                        .map((package_) => mapToAdminDeal(package_))
                        .filter((d) => d !== null);
                    setDeals(mapped);
                    setTotalPages(Math.max(1, pages));
                }
            }
            catch (error) {
                console.error("Failed to load instant deals", error);
                if (!cancelled) {
                    setDeals([]);
                    setTotalPages(1);
                }
            }
            finally {
                if (!cancelled) {
                    setLoading(false);
                    setIsInitialLoad(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [page, search, statusFilter, networkFilter, dateSort]);
    if (isInitialLoad)
        return React.createElement(LoadingFallback, null);
    return (React.createElement("div", { className: styles.page },
        React.createElement("div", { className: styles.body },
            React.createElement("div", { className: styles.pageHeader },
                React.createElement("h1", { className: styles.title }, "Instant Deals Won"),
                React.createElement("p", { className: styles.subtitle }, "Suppliers awarded deals chosen by customers through Predict Instant")),
            React.createElement("div", { className: styles.container },
                React.createElement("div", { className: styles.toolbar },
                    React.createElement("label", { className: styles.searchField },
                        React.createElement(SearchSm, { className: `${iconStyles.icon} ${iconStyles.searchSm}` }),
                        React.createElement("input", { type: "search", className: styles.searchInput, placeholder: "Search customers, deals...", value: searchInput, onChange: (event_) => setSearchInput(event_.target.value) })),
                    React.createElement("div", { className: styles.filters },
                        React.createElement(FilterSelect, { value: statusFilter, options: STATUS_OPTIONS, onChange: (v) => {
                                setStatusFilter(v);
                                setPage(1);
                            } }),
                        React.createElement(FilterSelect, { value: networkFilter, options: NETWORK_OPTIONS, onChange: (v) => {
                                setNetworkFilter(v);
                                setPage(1);
                            } }),
                        React.createElement(FilterSelect, { value: dateSort, options: DATE_OPTIONS, onChange: (v) => {
                                setDateSort(v);
                                setPage(1);
                            } }))),
                React.createElement(DealsTable, { deals: deals, loading: loading, currentPage: page, totalPages: totalPages, onViewDeal: (dealId) => {
                        const deal = deals.find((item) => item.id === dealId);
                        history.push({
                            pathname: GetPath(INSTANT_DEALS_WON_DETAIL, dealId),
                            state: { customerName: deal?.customerName },
                        });
                    }, onPreviousPage: () => setPage((p) => Math.max(1, p - 1)), onNextPage: () => setPage((p) => Math.min(totalPages, p + 1)) })))));
};
